/**
 * Calendly tracking helpers.
 * Uses Calendly's Popup Widget + postMessage listener to detect when
 * a booking is confirmed, then pushes a single dataLayer event
 * `calendly_bokning` with the Calendly booking ID. GTM owns the Meta
 * Pixel tag — this module does NOT call fbq directly.
 */

export const CALENDLY_URL = "https://calendly.com/aminah-aminahadin/30min";

function isCalendlyEvent(e) {
  return (
    e &&
    e.data &&
    typeof e.data === "object" &&
    typeof e.data.event === "string" &&
    e.data.event.indexOf("calendly") === 0
  );
}

/** Extract last URI segment, e.g. "abc-123" from "https://.../invitees/abc-123". */
function uuidFromUri(uri) {
  if (typeof uri !== "string") return null;
  const parts = uri.split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : null;
}

/** Pull a stable booking id out of Calendly's event_scheduled payload. */
function bookingIdFrom(payload) {
  if (!payload || typeof payload !== "object") return null;
  // Prefer the invitee URI (uniquely identifies the booked slot)
  return (
    uuidFromUri(payload?.invitee?.uri) ||
    uuidFromUri(payload?.event?.uri) ||
    null
  );
}

/**
 * Open Calendly's popup widget. Falls back to opening the URL in a new tab
 * if the Calendly script hasn't finished loading yet.
 */
export function openCalendly() {
  if (typeof window === "undefined") return;
  if (window.Calendly && typeof window.Calendly.initPopupWidget === "function") {
    window.Calendly.initPopupWidget({ url: CALENDLY_URL });
  } else {
    window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
  }
}

/**
 * Install a global listener that pushes a `calendly_bokning` dataLayer
 * event (with booking_id) and navigates to /thank-you when a Calendly
 * booking is confirmed. Safe to call multiple times.
 */
let installed = false;
export function installCalendlyTracking() {
  if (typeof window === "undefined" || installed) return;
  installed = true;

  window.addEventListener("message", (e) => {
    if (!isCalendlyEvent(e)) return;
    if (e.data.event !== "calendly.event_scheduled") return;

    const booking_id = bookingIdFrom(e.data.payload);

    // Navigate once — either when GTM reports its tags have fired
    // (eventCallback) or after a safety timeout, whichever is first.
    let navigated = false;
    const goToThanks = () => {
      if (navigated) return;
      navigated = true;
      window.location.href = "/thank-you";
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "calendly_bokning",
      booking_id,
      // GTM calls eventCallback once all tags for this event have fired.
      // eventTimeout is GTM's own safety cap on how long it waits for tags.
      eventCallback: goToThanks,
      eventTimeout: 1000,
    });

    // Client-side reserve in case GTM is blocked / never loaded
    setTimeout(goToThanks, 1500);
  });
}
